import { useEffect, useMemo, useRef, useState } from 'react'
import { MenuList, ListItem, Input } from '@clickguard/design-system'
import { AdAccountDTO } from 'api-models'
import { PlatformIcon } from 'components/platform-icon'
import Announcement02 from '@untitled-ui/icons-react/build/esm/Announcement02'
import { BreadcrumbTrigger } from './breadcrumb-trigger'
import { Radio } from './radio'
import { sortCampaigns } from './campaign-order'
import { CampaignStatusIcon } from './campaign-status-icon'
import { CampaignStatusPills } from './campaign-status-pills'
import {
	CAMPAIGN_STATUS_FILTERS,
	CampaignDTO,
	CampaignStatusFilter,
	countCampaignsByStatus,
	getCampaignStatusGroup,
	CampaignStatusGroup
} from './campaign-status'

interface CampaignsSelectorSingleProps {
	campaigns: CampaignDTO[]
	selectedCampaign: CampaignDTO | null
	adAccounts: AdAccountDTO[]
	onSelect: (campaign: CampaignDTO) => void
}

export const CampaignsSelectorSingle = ({
	campaigns,
	selectedCampaign,
	adAccounts,
	onSelect
}: CampaignsSelectorSingleProps) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	// All statuses are shown by default (DEV-2437); the pills narrow the list.
	const [activeStatuses, setActiveStatuses] = useState<CampaignStatusFilter[]>(
		CAMPAIGN_STATUS_FILTERS
	)
	const containerRef = useRef<HTMLDivElement>(null)

	const adAccountPlatformMap = useMemo(() => {
		const map: Record<string, string> = {}
		adAccounts.forEach((acc) => {
			map[acc.id] = acc.platform
		})
		return map
	}, [adAccounts])

	const statusCounts = useMemo(
		() => countCampaignsByStatus(campaigns),
		[campaigns]
	)

	const visibleCampaigns = useMemo(() => {
		let filtered = campaigns
		// With every pill on (the default), show everything — including campaigns
		// whose status maps to no pill group. Narrowing filters by group.
		if (activeStatuses.length < CAMPAIGN_STATUS_FILTERS.length) {
			filtered = filtered.filter((c) => {
				const group = getCampaignStatusGroup(c)
				// The pills only offer Active and Paused, so a REMOVED campaign matches
				// neither and is filtered out — the behaviour this had before REMOVED
				// became a bucket of its own rather than `null`.
				return (
					group !== null &&
					(activeStatuses as CampaignStatusGroup[]).includes(group)
				)
			})
		}
		if (searchQuery) {
			filtered = filtered.filter((c) =>
				c.name.toLowerCase().includes(searchQuery.toLowerCase())
			)
		}
		return filtered
	}, [campaigns, activeStatuses, searchQuery])

	const sortedVisibleCampaigns = useMemo(
		() => sortCampaigns(visibleCampaigns, adAccountPlatformMap),
		[visibleCampaigns, adAccountPlatformMap]
	)

	const toggleStatus = (status: CampaignStatusFilter) => {
		setActiveStatuses((prev) =>
			prev.includes(status)
				? prev.filter((s) => s !== status)
				: [...prev, status]
		)
	}

	useEffect(() => {
		if (!isOpen) return
		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsOpen(false)
				setSearchQuery('')
			}
		}
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setIsOpen(false)
				setSearchQuery('')
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleEscape)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [isOpen])

	const handleOpen = () => {
		setSearchQuery('')
		setActiveStatuses(CAMPAIGN_STATUS_FILTERS)
		setIsOpen(true)
	}

	const handlePick = (campaign: CampaignDTO) => {
		onSelect(campaign)
		setIsOpen(false)
		setSearchQuery('')
	}

	const selectedPlatform =
		(selectedCampaign &&
			(adAccountPlatformMap[selectedCampaign.ad_account_id] as
				| 'GADS'
				| 'FADS'
				| 'BADS')) ||
		null

	const label = selectedCampaign ? selectedCampaign.name : 'Select campaign'

	return (
		<div className="relative flex min-w-0" ref={containerRef}>
			<BreadcrumbTrigger
				icon={
					selectedPlatform ? (
						<PlatformIcon
							platform={selectedPlatform}
							className="w-4 h-4"
						/>
					) : (
						<Announcement02 className="size-4 text-[var(--text-secondary,#535255)]" />
					)
				}
				label={label}
				onClick={() => {
					if (campaigns.length === 0) return
					isOpen ? setIsOpen(false) : handleOpen()
				}}
				isOpen={isOpen}
			/>

			{isOpen && (
				<div className="absolute top-full left-0 mt-1 z-50">
					{/* Matches the multi-select width (Figma 393px + slack for Chrome's
				    wider pill-count digit rendering). */}
				<MenuList className="w-[406px] !p-[10px]" role="listbox">
						<div className="flex items-center gap-[4px] px-[4px] leading-[16px]">
							<span className="font-['Host_Grotesk',sans-serif] text-[12px] leading-[16px] text-[var(--text-secondary,#535255)] whitespace-nowrap">
								Campaigns
							</span>
						</div>

						{/* Active / Paused / Removed status filter pills */}
						<div className="mt-[12px]">
							<CampaignStatusPills
								counts={statusCounts}
								isSelected={(status) =>
									activeStatuses.includes(status)
								}
								onToggle={toggleStatus}
							/>
						</div>

						<div className="mt-[12px]">
							<Input
								size="sm"
								state="default"
								placeholder="Search for campaigns..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
						</div>

						{/* Full-bleed: cancel the MenuList's 10px padding so the list
						    and its scrollbar reach the popover edges. */}
						<div className="mt-[4px] max-h-[240px] overflow-y-auto -mx-[10px] -mb-[10px]">
							{visibleCampaigns.length > 0 ? (
								sortedVisibleCampaigns.map((campaign) => {
									const isChecked =
										selectedCampaign?.id === campaign.id
									const platform =
										(adAccountPlatformMap[campaign.ad_account_id] as
											| 'GADS'
											| 'FADS'
											| 'BADS') || 'GADS'
									const statusGroup = getCampaignStatusGroup(campaign)
									return (
										<ListItem
											key={campaign.id}
											label={campaign.name}
											leadingControl={
												<div onClick={(e) => e.stopPropagation()}>
													<Radio checked={isChecked} />
												</div>
											}
											leadingIcon={
												<PlatformIcon
													platform={platform}
													className="w-4 h-4"
												/>
											}
											trailingIcon={
												statusGroup ? (
													<CampaignStatusIcon
														status={statusGroup}
													/>
												) : undefined
											}
											onClick={() => handlePick(campaign)}
										/>
									)
								})
							) : (
								<div className="px-[8px] py-[8px]">
									<span className="font-['Host_Grotesk',sans-serif] text-[14px] leading-[18px] text-[var(--text-tertiary,#9f9ea3)]">
										No campaigns found
									</span>
								</div>
							)}
						</div>
					</MenuList>
				</div>
			)}
		</div>
	)
}
